import React from 'react';
import PageTransition from '@/components/ui/PageTransition';
import {
  Typography,
  Button,
  Row,
  Col,
  Empty,
  Flex,
  Space,
  Skeleton,
  Card,
  Tag,
  Descriptions,
  Result,
  Alert,
  theme
} from 'antd';
import type { DescriptionsProps } from 'antd';
import {
  ArrowLeftOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  LinkOutlined,
  FileTextOutlined,
  TrophyOutlined,
  GlobalOutlined,
  DatabaseOutlined,
  MessageOutlined,
  SafetyCertificateOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  KeyOutlined,
  DesktopOutlined,
  IdcardOutlined,
  ApiOutlined
} from '@ant-design/icons';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useLeadQuery } from '@/api/mcp-crm/queries';
import { DeleteEntityModal } from '@/components/shared';
import { LeadContactModal } from '@/components/leads/LeadContactModal';
import { LeadActionBar } from '@/components/leads/LeadActionBar';
import { LeadTimeline } from '@/components/leads/LeadTimeline';
import { getLeadChannelConfig, getLeadStatusConfig } from '@/components/leads/leadTagConfig';
import { useLeadPermissions } from '@/components/leads/useLeadPermissions';
import { useLeadDetailActions } from '@/components/leads/useLeadDetailActions';
import { threadKeyForLead } from '@/components/threads/utils';
import { useTranslation } from 'react-i18next';
import { StructuredNoteContent } from '@/components/structured-note';
import { DetailPageHeader } from '@/components/ui/DetailPageHeader';

const { Text } = Typography;

export const Route = createFileRoute('/manage/leads/$leadId')({
  component: LeadDetailComponent,
});

function LeadDetailComponent() {
  const { t } = useTranslation('manage');
  const { leadId } = useParams({ from: '/manage/leads/$leadId' });
  const { token } = theme.useToken();

  const {
    data: lead,
    isLoading,
  } = useLeadQuery(leadId);
  const permissions = useLeadPermissions(lead);
  const {
    convertMutation,
    requestVerificationMutation,
    updateContactMutation,
    contactModalOpen,
    setContactModalOpen,
    loadLeadDeletionPreview,
    deleteLeadsMutation,
    leadDeletion,
    handleConvertLead,
    handleRequestVerification,
    handleSaveContact
  } = useLeadDetailActions(leadId, lead);

  // Loading skeleton
  if (isLoading) {
    return (
      <PageTransition animation="fadeInUp">
        <Flex vertical gap={token.marginMD}>
          <Skeleton active avatar paragraph={{ rows: 1 }} />
          <Row gutter={[token.marginMD, token.marginMD]}>
            <Col xs={24} lg={16}>
              <Card><Skeleton active paragraph={{ rows: 6 }} /></Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card><Skeleton active paragraph={{ rows: 6 }} /></Card>
            </Col>
          </Row>
        </Flex>
      </PageTransition>
    );
  }

  // Not found state
  if (!lead) {
    return (
      <PageTransition animation="fadeInUp">
        <Result
          status="404"
          title={t('leadDetail.notFound')}
          subTitle={t('leadDetail.notFoundDescription')}
          extra={
            <Link to="/manage/leads">
              <Button type="primary" icon={<ArrowLeftOutlined />}>
                {t('leadDetail.backToLeads')}
              </Button>
            </Link>
          }
        />
      </PageTransition>
    );
  }

  const statusConfig = getLeadStatusConfig(lead.status);
  const channelConfig = getLeadChannelConfig(lead.channel);
  const { isConverted, conversionBlockReason, canEditContact, verificationPending } = permissions;

  const renderVerification = (verified: boolean, verifiedLabel: string, notVerifiedLabel: string) =>
    verified
      ? <Tag icon={<CheckCircleOutlined />} color="success">{verifiedLabel}</Tag>
      : <Tag icon={<ExclamationCircleOutlined />} color="warning">{notVerifiedLabel}</Tag>;

  const renderJsonValue = (value: unknown) =>
    typeof value === 'object' && value !== null
      ? <Text code>{JSON.stringify(value)}</Text>
      : <Tag color="geekblue">{String(value)}</Tag>;
  const iconLabel = (icon: React.ReactNode, label: string) => <Space>{icon}{label}</Space>;

  const contactItems: DescriptionsProps['items'] = [
    {
      key: 'email',
      label: <Space><MailOutlined />{t('leadDetail.sidebar.email')}</Space>,
      children: lead.email ? (
        <Space wrap>
          <Text copyable>{lead.email}</Text>
          {renderVerification(lead.emailVerified, t('leadDetail.sidebar.emailVerified'), t('leadDetail.sidebar.emailNotVerified'))}
        </Space>
      ) : (
        <Text type="secondary">{t('leadDetail.sidebar.noEmail')}</Text>
      )
    },
    {
      key: 'phone',
      label: <Space><PhoneOutlined />{t('leadDetail.sidebar.phone')}</Space>,
      children: lead.phone ? (
        <Space wrap>
          <Text copyable>{lead.phone}</Text>
          {renderVerification(lead.phoneVerified, t('leadDetail.sidebar.phoneVerified'), t('leadDetail.sidebar.phoneNotVerified'))}
        </Space>
      ) : (
        <Text type="secondary">{t('leadDetail.sidebar.noPhone')}</Text>
      )
    }
  ];

  const sourceItems: DescriptionsProps['items'] = [
    {
      key: 'channel',
      label: iconLabel(<ApiOutlined />, t('leadDetail.sidebar.channel')),
      children: (
        <Tag icon={channelConfig.icon} color={channelConfig.color}>
          {t(`leads.channel.${lead.channel}`, { defaultValue: lead.channel })}
        </Tag>
      )
    },
    {
      key: 'sourceThread',
      label: iconLabel(<MessageOutlined />, t('leadDetail.sidebar.sourceThread')),
      children: lead.sourceThreadId
        ? <Text code copyable>{lead.sourceThreadId}</Text>
        : <Text type="secondary">{t('leadDetail.sidebar.noThread')}</Text>
    },
    {
      key: 'sourceSession',
      label: iconLabel(<KeyOutlined />, t('leadDetail.sidebar.sourceSession')),
      children: lead.sourceSessionId
        ? <Text code copyable>{lead.sourceSessionId}</Text>
        : <Text type="secondary">{t('leadDetail.sidebar.noSession')}</Text>
    }
  ];

  const fingerprintEntries = lead.fingerprint ? Object.entries(lead.fingerprint) : [];
  const identityItems: DescriptionsProps['items'] = [
    ...(lead.lastKnownIp
      ? [{ key: 'ip', label: iconLabel(<GlobalOutlined />, t('leadDetail.sidebar.lastKnownIp')), children: <Text code copyable>{lead.lastKnownIp}</Text> }]
      : []),
    ...(lead.lastKnownUserAgent
      ? [{ key: 'ua', label: iconLabel(<DesktopOutlined />, t('leadDetail.sidebar.lastKnownUserAgent')), children: <Text type="secondary">{lead.lastKnownUserAgent}</Text> }]
      : []),
    // Usually the same session as the source session; only list it when it differs.
    ...(lead.authenticatedSessionId && lead.authenticatedSessionId !== lead.sourceSessionId
      ? [{ key: 'authSession', label: iconLabel(<KeyOutlined />, t('leadDetail.sidebar.authenticatedSessionId')), children: <Text code copyable>{lead.authenticatedSessionId}</Text> }]
      : []),
    ...(fingerprintEntries.length > 0
      ? [{
          key: 'fingerprint',
          label: iconLabel(<IdcardOutlined />, t('leadDetail.sidebar.fingerprint')),
          children: (
            <Space size={[4, 8]} wrap>
              {fingerprintEntries.map(([key, value]) => (
                <Tag key={key} color="purple">
                  {key}: {typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)}
                </Tag>
              ))}
            </Space>
          )
        }]
      : [])
  ];

  const metadataItems: DescriptionsProps['items'] = Object.entries(lead.metadata ?? {}).map(([key, value]) => ({
    key,
    label: key,
    children: renderJsonValue(value)
  }));

  const statusAlert = isConverted ? (
    <Alert
      type="success"
      showIcon
      icon={<TrophyOutlined />}
      title={t('leadDetail.content.convertedCustomer')}
      description={<Text copyable>{lead.convertedCustomerId}</Text>}
    />
  ) : verificationPending ? (
    <Alert type="info" showIcon icon={<SyncOutlined spin />} title={t('leadDetail.content.requestVerificationPending')} />
  ) : conversionBlockReason ? (
    <Alert type="warning" showIcon title={conversionBlockReason} />
  ) : null;

  return (
    <PageTransition animation="fadeInUp">
      <DetailPageHeader
        icon={<UserOutlined />}
        title={lead.name || t('leads.table.anonymous')}
        meta={
          <>
            <Tag icon={statusConfig.icon} color={statusConfig.color}>
              {t(`leads.status.${lead.status}`, { defaultValue: lead.status })}
            </Tag>
            <Tag icon={channelConfig.icon} color={channelConfig.color}>
              {t(`leads.channel.${lead.channel}`, { defaultValue: lead.channel })}
            </Tag>
            <Text type="secondary" copyable={{ text: lead.id, tooltips: [t('leadDetail.header.copyLeadId'), t('leadDetail.leadIdCopied')] }}>
              {t('leadDetail.header.id', { id: `${lead.id.slice(0, 8)}...` })}
            </Text>
          </>
        }
        actions={
          <LeadActionBar
            permissions={permissions}
            requestingVerification={requestVerificationMutation.isPending}
            converting={convertMutation.isPending}
            onRequestVerification={handleRequestVerification}
            onConvert={handleConvertLead}
            onDelete={() => leadDeletion.request([lead.id])}
          />
        }
        backTo="/manage/leads"
        backLabel={t('leadDetail.backToLeads')}
      />

      <Flex vertical gap={token.marginMD}>
        <Row gutter={[token.marginMD, token.marginMD]}>
          {/* Main content */}
          <Col xs={24} lg={16}>
            <Flex vertical gap={token.marginMD}>
              {statusAlert}
              <Card
                title={<Space><UserOutlined />{t('leadDetail.sidebar.contactInfo')}</Space>}
                extra={canEditContact && (
                  <Button type="link" icon={<EditOutlined />} onClick={() => setContactModalOpen(true)}>
                    {t('leadDetail.contactEdit.edit')}
                  </Button>
                )}
              >
                <Descriptions column={1} items={contactItems} />
              </Card>

              <Card title={<Space><FileTextOutlined />{t('leadDetail.content.notes')}</Space>}>
                {lead.notes
                  ? <StructuredNoteContent value={lead.notes} />
                  : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('leadDetail.content.noNotes')} />}
              </Card>

              <Card title={<Space><DatabaseOutlined />{t('leadDetail.content.metadata')}</Space>}>
                {metadataItems.length > 0
                  ? <Descriptions column={{ xs: 1, md: 2 }} size="small" items={metadataItems} />
                  : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('leadDetail.content.noMetadata')} />}
              </Card>
            </Flex>
          </Col>

          {/* Sidebar */}
          <Col xs={24} lg={8}>
            <Flex vertical gap={token.marginMD}>
              <Card
                title={<Space><LinkOutlined />{t('leadDetail.sidebar.source')}</Space>}
                extra={lead.sourceThreadId && (
                  <Link to={`/manage/threads/${threadKeyForLead(lead.channel, lead.sourceThreadId)}`}>
                    <Button type="primary" icon={<MessageOutlined />}>{t('leadDetail.sidebar.viewThread')}</Button>
                  </Link>
                )}
              >
                <Descriptions column={1} size="small" layout="vertical" items={sourceItems} />
              </Card>

              <Card title={<Space><ClockCircleOutlined />{t('leadDetail.sidebar.timeline')}</Space>}>
                <LeadTimeline lead={lead} isConverted={isConverted} />
              </Card>

              {identityItems.length > 0 && (
                <Card title={<Space><SafetyCertificateOutlined />{t('leadDetail.sidebar.sessionIdentity')}</Space>}>
                  <Descriptions column={1} size="small" layout="vertical" items={identityItems} />
                </Card>
              )}
            </Flex>
          </Col>
        </Row>
      </Flex>

      <LeadContactModal
        open={contactModalOpen}
        loading={updateContactMutation.isPending}
        email={lead.email}
        phone={lead.phone}
        onCancel={() => setContactModalOpen(false)}
        onSubmit={handleSaveContact}
      />
      <DeleteEntityModal
        entity="leads"
        {...leadDeletion.modalState}
        loadPreview={loadLeadDeletionPreview}
        onConfirm={(leadIds) => deleteLeadsMutation.mutateAsync(leadIds)}
        isDeleting={deleteLeadsMutation.isPending}
      />
    </PageTransition>
  );
}
